import {Col, Empty, Row, Spin} from "antd";
import React, {ReactElement, useMemo} from "react";
import {isEqual} from "underscore";
import InViewRenderItem from "@components/grid/InViewRenderItem";

export const PAGINATION_NONE = 0;
export const PAGINATION_PAGES = 1;
export const PAGINATION_SHOW_MORE = 2;

interface GridProps {
    isListGrid: boolean;
    loading: boolean;
    className?: string;
    dataSource: any[];
    IssueRender: (props: any) => ReactElement;
    expandedItem?: any;
    ExpandedRender?: (props: any) => ReactElement;
    pagination?: React.ReactNode;
    noDataMessage?: string;
    compareExpandedItem?: (a: any, b: any) => boolean;
    getItemProps: (item: any) => any;
}

const defaultProps: GridProps = {
    isListGrid: false,
    dataSource: [],
    loading: false,
    IssueRender: () => (<>No IssueRender was set</>),
    noDataMessage: "Use the + Add Button Above To Find And Add Comics to Your Collection",
    getItemProps: (item) => ({}),
};

const Grid = ({
    isListGrid,
    className,
    loading,
    dataSource,
    IssueRender,
    ExpandedRender,
    pagination,
    expandedItem,
    noDataMessage,
    compareExpandedItem,
    getItemProps,
}: GridProps) => {

    const hasExpandedRow = useMemo<boolean>(() => { return !!ExpandedRender; }, [ExpandedRender]);
    const comparison = useMemo(() => {
        return compareExpandedItem ? compareExpandedItem : (a, b) => {
            // In the case we go with default, we'll assume the objects have an id property.
            if (a == null || b == null) {
                return false;
            }
            else if (!!a.id && !!b.id) {
                return a.id === b.id;
            }
            return a === b;
        };
    }, [compareExpandedItem]);

    return (
        <Spin size="large" spinning={loading}>
            <Row justify="center" className={className}>
                {dataSource?.length > 0 ? (
                    <div className={`covr-grid covr-grid xs sm md lg xl row-gap-0 ` + (isListGrid ? ' list-grid' : '')} >
                        {dataSource?.map((issue) => {
                            const itemProps = getItemProps(issue);
                            return (<GridItemMemoized
                                key={itemProps.key}
                                issue={issue}
                                IssueRender={IssueRender}
                                hasExpandedRow={hasExpandedRow}
                                isExpandedItem={comparison(expandedItem, issue)}
                                ExpandedRender={ExpandedRender}
                                itemProps={itemProps}
                            />);
                        })}
                    </div>
                ) : (
                    <Col>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        <p className="text-light-grey">{noDataMessage}</p>
                    </Col>
                )}
            </Row>
            {!loading && pagination
                && <Row className={'pad-top-15'} justify="end">{pagination}</Row>}
        </Spin>
    );
};

const GridItem = ({ issue, IssueRender, itemProps, hasExpandedRow, isExpandedItem, ExpandedRender }) => {
    return (
        <article className={isExpandedItem ? `covr-grid-article ${hasExpandedRow ? "expandable" : "active"}` : "covr-grid-article"}>
            {<InViewRenderItem><IssueRender item={issue} {...itemProps}/></InViewRenderItem>}
            {hasExpandedRow && ExpandedRender && isExpandedItem && (
                <Row justify="center" className="covr-grid-tray-content">
                    <Col><ExpandedRender item={issue} IssueRender={IssueRender}/></Col>
                </Row>
            )}
        </article>
    );
}

const GridItemMemoized = React.memo(GridItem, (prevProps, nextProps) => {
    return isEqual(prevProps.issue, nextProps.issue)
        && prevProps.isExpandedItem === nextProps.isExpandedItem
        && isEqual(prevProps.itemProps, nextProps.itemProps)
});

Grid.defaultProps = defaultProps;

export default Grid;
